import React, { useState } from "react";
import {
  Box,
  Text,
  IconButton,
  Collapse,
  Grid,
  HStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { IBillingLocationReport, IBillingReportData } from "../../../api/types";

type Props = {
  locations: IBillingLocationReport[];
};

const formatKWh = (value?: number) =>
  typeof value === "number" ? `${value.toFixed(2)}` : "-";

const formatCost = (value?: number) =>
  typeof value === "number" ? `${value.toFixed(2)}` : "-";

const unmeasuredEnergyRow = (device: IBillingReportData, indent: number) => {
  const childTotalKWh = device.children.reduce((sum: number, d: any) => sum + (d.value || 0), 0)
  const childTotalCost = device.children.reduce((sum: number, d: any) => sum + (d.total_cost || 0), 0)

  const unmeasuredKWh = (device.value || 0) - childTotalKWh
  const unmeasuredCost = (device.total_cost || 0) - childTotalCost

  const percentDiff = (unmeasuredKWh / (device.value || 1)) * 100

  if (percentDiff >= 1) {
    return (
      <DeviceRow
        device={{
          id: `unmeasured-${device.id}`,
          title: "Unmeasured",
          value: unmeasuredKWh,
          total_cost: unmeasuredCost,
          children: [],
        }}
        indent={indent + 1}
        isChild
      />
    )
  }
}

const DeviceRow = ({
  device,
  indent = 0,
  isOpen,
  onToggle,
  isChild = false,
}: {
  device: any;
  indent?: number;
  isOpen?: boolean;
  onToggle?: () => void;
  isChild?: boolean;
}) => {
  const isExpandable = device.children && device.children.length > 0;

  return (
    <Box
      onClick={isExpandable ? onToggle : undefined}
      _hover={{ bg: "#F9F9F9" }}
      cursor={isExpandable ? "pointer" : "default"}
      bg={isOpen ? "#f5f6f7" : "transparent"}
    >
      <Grid
        templateColumns="1fr 1fr 1fr"
        alignItems="center"
        pl={indent * 2}
        pr={2}
        py={2}
        borderBottom="1px solid"
        borderColor="gray.100"
      >
        <HStack spacing={2}>
          {isExpandable && (
            <IconButton
              icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
              aria-label="Toggle"
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onToggle?.();
              }}
            />
          )}
          <Text fontSize="14px" fontWeight="400" fontFamily="Inter" color="#484848">
            {device.title ?? "Unnamed device"}
          </Text>
        </HStack>

        <Text fontSize="14px" textAlign="right" fontWeight="700" color="#707070" fontFamily="Inter">
          {formatKWh(device.value)}
          <span style={{ fontWeight: 400, fontSize: "12px", marginLeft: "5px" }}>kWh</span>
        </Text>

        <Text fontSize="14px" textAlign="right" fontWeight="700" color="#707070" fontFamily="Inter">
          {formatCost(device.total_cost)}
          <span style={{ fontWeight: 400, fontSize: "12px", marginLeft: "5px" }}>лв</span>
        </Text>
      </Grid>
    </Box>
  );
};

const renderChildrenRecursive = (
  children: any[],
  expandedDevices: Record<string, boolean>,
  toggleDevice: (id: number) => void,
  indent: number
) =>
  children?.map((child) => {
    const isOpen = expandedDevices[child.id] ?? false;
    const hasChildren = child.children && child.children.length > 0;

    return (
      <Box key={child.id} bg={isOpen ? "#f5f6f7" : "transparent"}>
        <DeviceRow
          device={child}
          isChild
          indent={indent}
          isOpen={isOpen}
          onToggle={() => toggleDevice(child.id)}
        />
        {hasChildren && (
          <Collapse in={isOpen} animateOpacity>
            <>
              {renderChildrenRecursive(child.children, expandedDevices, toggleDevice, indent + 1)}
              {unmeasuredEnergyRow(child, indent)}
            </>
          </Collapse>
        )}
      </Box>
    );
  });

export const DevicesBreakdown = ({ locations }: Props) => {
  const [expandedDevices, setExpandedDevices] = useState<Record<string, boolean>>({});

  const toggleDevice = (deviceId: number) => {
    setExpandedDevices((prev) => ({
      ...prev,
      [deviceId]: !prev[deviceId],
    }));
  };

  if (!locations || locations.length === 0) {
    return (
      <Box bg="white" mt={5} p={5} rounded="xl" boxShadow="sm" w="100%">
        <Text>No data available.</Text>
      </Box>
    );
  }

  return (
    <Box bg="white" mt={5} p={5} rounded="xl" boxShadow="sm" w="100%">
      {locations?.map((location) => {
        return (
          <Box key={location.title} mb={8}>
            <Text fontSize="18px" fontWeight="700" mb={4}>
              {location.title}
            </Text>

            {/* Table Header */}
            <Grid
              templateColumns="1fr 1fr 1fr"
              alignItems="center"
              mb={2}
              mt={2}
              pr={2}
              pl={0}
            >
              <Text fontSize="13px" fontWeight="700" color="#9B9B9B">
                Device
              </Text>
              <Text fontSize="13px" fontWeight="700" color="#9B9B9B" textAlign="right">
                Total consumed energy
              </Text>
              <Text fontSize="13px" fontWeight="700" color="#9B9B9B" textAlign="right">
                Cost for consumed energy
              </Text>
            </Grid>

            {/* Devices */}
            {location?.children.map((device) => {
              const isOpen = expandedDevices[device.id] ?? false;
              const hasChildren = device.children && device.children.length > 0;

              return (
                <Box key={device.id} bg={isOpen ? "#f5f6f7" : "transparent"}>
                  <DeviceRow
                    device={device}
                    isOpen={isOpen}
                    onToggle={() => toggleDevice(device.id)}
                  />
                  {hasChildren && (
                    <Collapse in={isOpen} animateOpacity>
                      {renderChildrenRecursive(device.children, expandedDevices, toggleDevice, 1)}
                      {unmeasuredEnergyRow(device, 0)}
                    </Collapse>
                  )}
                </Box>
              );
            })}

            {/* Total Row */}
            <Grid
              templateColumns="1fr 1fr 1fr"
              alignItems="center"
              pt={3}
              mt={2}
              fontWeight="bold"
            >
              <Text fontSize="14px" color="gray.700" />
              <Text fontSize="18px" textAlign="right" color="#484848" fontFamily="Inter">
                {formatKWh(location.value)}
                <span style={{ fontWeight: 400, fontSize: "12px", marginLeft: "5px" }}>kWh</span>
              </Text>
              <Text fontSize="18px" textAlign="right" color="#484848" fontFamily="Inter">
                {formatCost(location.total_cost)}
                <span style={{ fontWeight: 400, fontSize: "12px", marginLeft: "5px" }}>лв</span>
              </Text>
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};
